import React, { createContext, useContext } from 'react';

import {
  StyledCounter,
  StyledIcon,
  StyledIndicator,
  StyledPanel,
  StyledTab,
  StyledTabList,
  StyledTabListScroller,
  StyledTabsRoot,
} from './Tabs.styled';
import type {
  TabListProps,
  TabPanelProps,
  TabProps,
  TabsOrientation,
  TabsRootProps,
  TabsSize,
  TabsVariant,
} from './types';

// ---------------------------------------------------------------------------
// Context — propagates size/variant from Root → List/Tab without prop drilling
// ---------------------------------------------------------------------------

interface TabsContextValue {
  size: TabsSize;
  variant: TabsVariant;
  fullWidth: boolean;
  orientation: TabsOrientation;
}

const TabsContext = createContext<TabsContextValue>({
  size: 'md',
  variant: 'boxed',
  fullWidth: false,
  orientation: 'horizontal',
});

// ---------------------------------------------------------------------------
// Tabs.Root
// ---------------------------------------------------------------------------

const Root: React.FC<TabsRootProps> = ({
  value,
  onValueChange,
  defaultValue,
  size = 'md',
  variant = 'boxed',
  fullWidth = false,
  orientation = 'horizontal',
  className,
  children,
}) => (
  <TabsContext.Provider value={{ size, variant, fullWidth, orientation }}>
    <StyledTabsRoot
      value={value}
      onValueChange={onValueChange}
      defaultValue={defaultValue}
      orientation={orientation}
      className={className}
      $fullWidth={fullWidth}
    >
      {children}
    </StyledTabsRoot>
  </TabsContext.Provider>
);

Root.displayName = 'Tabs.Root';

// ---------------------------------------------------------------------------
// Tabs.List
// ---------------------------------------------------------------------------

const List: React.FC<TabListProps> = ({ className, children }) => {
  const { size, variant, fullWidth, orientation } = useContext(TabsContext);

  return (
    <StyledTabListScroller $fullWidth={fullWidth} $orientation={orientation}>
      <StyledTabList
        $size={size}
        $variant={variant}
        $fullWidth={fullWidth}
        $orientation={orientation}
        className={className}
      >
        {children}
        {variant === 'bordered' && <StyledIndicator $orientation={orientation} />}
      </StyledTabList>
    </StyledTabListScroller>
  );
};

List.displayName = 'Tabs.List';

// ---------------------------------------------------------------------------
// Tabs.Tab
// ---------------------------------------------------------------------------

const Tab: React.FC<TabProps> = ({ value, disabled, icon, label, counter, className }) => {
  const { size, variant, fullWidth, orientation } = useContext(TabsContext);

  return (
    <StyledTab
      value={value}
      disabled={disabled}
      $size={size}
      $variant={variant}
      $fullWidth={fullWidth}
      $orientation={orientation}
      className={className}
    >
      {icon && <StyledIcon $size={size}>{icon}</StyledIcon>}
      {label != null && <span>{label}</span>}
      {counter != null && <StyledCounter>{counter}</StyledCounter>}
    </StyledTab>
  );
};

Tab.displayName = 'Tabs.Tab';

// ---------------------------------------------------------------------------
// Tabs.Panel
// ---------------------------------------------------------------------------

const Panel: React.FC<TabPanelProps> = ({ value, keepMounted, className, children }) => (
  <StyledPanel value={value} keepMounted={keepMounted} className={className}>
    {children}
  </StyledPanel>
);

Panel.displayName = 'Tabs.Panel';

// ---------------------------------------------------------------------------
// Compound export
// ---------------------------------------------------------------------------

export const Tabs = { Root, List, Tab, Panel };

Tabs.Root.displayName = 'Tabs';
